import { useEffect, useMemo, useRef, useState } from "react";

import { EPG_REANCHOR_AFTER_BACKGROUND_MS } from "$src/core/constants/epg-day-range";
import useAppStateEffect from "$src/core/hooks/use-app-state-effect";
import type { EPGConfigDto, EPGDto } from "$src/core/models/dto";
import SessionStorage from "$src/core/services/session-storage";
import filterProgramsForDay from "$src/core/utils/epg/filter-programs-for-day";
import getDayBoundsForOffset from "$src/core/utils/epg/get-day-for-offset";
import layoutProgramsFixedRows from "$src/core/utils/epg/layout-programs-fixed-rows";
import parseEpgUiSession from "$src/core/utils/epg/parse-epg-ui-session";

export type UseEpgDayModelParams = {
  epg: EPGDto | undefined;
  epgConfig: EPGConfigDto | undefined;
  defaults: { daysBefore: number; daysAfter: number };
  timeZone: string;
};

export const useEpgDayModel = ({
  epg,
  epgConfig,
  defaults,
  timeZone,
}: UseEpgDayModelParams) => {
  const [dayReferenceMs, setDayReferenceMs] = useState(() => Date.now());
  const hiddenAtMsRef = useRef<number | null>(null);

  /* A long background can outlast the schedule, so re-anchor the day to now. */
  useAppStateEffect((state) => {
    if (state !== "active") {
      hiddenAtMsRef.current = Date.now();
      return;
    }

    const hiddenAtMs = hiddenAtMsRef.current;
    hiddenAtMsRef.current = null;

    if (
      hiddenAtMs != null &&
      Date.now() - hiddenAtMs >= EPG_REANCHOR_AFTER_BACKGROUND_MS
    ) {
      setDayReferenceMs(Date.now());
    }
  }, []);
  const readPersistedEpgUi = () =>
    parseEpgUiSession(SessionStorage.get("epg.ui"));

  const [selectedDateOffset, setSelectedDateOffset] = useState(
    () => readPersistedEpgUi().selectedDateOffset,
  );
  const [userSelectedChannelId, setUserSelectedChannelId] = useState(
    () => readPersistedEpgUi().userSelectedChannelId,
  );

  useEffect(() => {
    SessionStorage.set("epg.ui", {
      selectedDateOffset,
      userSelectedChannelId,
    });
  }, [selectedDateOffset, userSelectedChannelId]);

  const daysBefore = epgConfig?.daysBefore ?? defaults.daysBefore;
  const daysAfter = epgConfig?.daysAfter ?? defaults.daysAfter;

  const dayBounds = useMemo(
    () =>
      getDayBoundsForOffset(selectedDateOffset, {
        daysAfter,
        daysBefore,
        timeZone,
        now: new Date(dayReferenceMs),
      }),
    [dayReferenceMs, daysAfter, daysBefore, selectedDateOffset, timeZone],
  );

  const dayChannels = useMemo(() => {
    if (!epg?.channels.length || !dayBounds) {
      return [];
    }

    const dayStartMs = dayBounds.dayStart.getTime();
    const dayEndMs = dayBounds.dayEnd.getTime();

    // Preserve the CMS `EPGChannelList` stack order (KAHI-268): do not re-sort.
    return epg.channels.map((channel) => {
      const programs = filterProgramsForDay(
        channel.programs,
        dayStartMs,
        dayEndMs,
      );
      return {
        ...channel,
        programs,
        programLayouts: layoutProgramsFixedRows(programs),
      };
    });
  }, [dayBounds, epg]);

  const selectedChannelId = useMemo(() => {
    if (!dayChannels.length) {
      return null;
    }
    if (
      userSelectedChannelId != null &&
      dayChannels.some((c) => c.id === userSelectedChannelId)
    ) {
      return userSelectedChannelId;
    }
    return dayChannels[0].id;
  }, [dayChannels, userSelectedChannelId]);

  const selectedChannelIndex = useMemo(
    () => dayChannels.findIndex((c) => c.id === selectedChannelId),
    [dayChannels, selectedChannelId],
  );

  const timelineScrollAnchor = useMemo(() => {
    if (!dayBounds) {
      return undefined;
    }
    return {
      selectedDateOffset,
      dayStart: dayBounds.dayStart,
      primeTime: epgConfig?.primeTime,
      timeZone,
      dayReferenceMs,
    };
  }, [
    dayBounds,
    dayReferenceMs,
    epgConfig?.primeTime,
    selectedDateOffset,
    timeZone,
  ]);

  return {
    daysBefore,
    daysAfter,
    selectedDateOffset,
    setSelectedDateOffset,
    dayBounds,
    dayChannels,
    selectedChannelId,
    selectedChannelIndex,
    userSelectedChannelId,
    setUserSelectedChannelId,
    timelineScrollAnchor,
  };
};
